import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from './api';
import { toast } from 'sonner';
import { ArrowLeft, Save } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useNavigate, useParams } from 'react-router-dom';

export default function EmployeeEdit() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    user_id: '',
    full_name: '',
    email: '',
    phone: '',
    department: '',
    position: '',
    join_date: '',
    employment_type: 'full_time',
    salary_basic: 0,
    salary_allowances: 0,
    ic_number: '',
    nationality: 'malaysian',
    marital_status: 'single',
    spouse_working: false,
    number_of_children: 0,
    disabled_self: false,
    disabled_spouse: false,
    disabled_children: 0,
    epf_number: '',
    socso_number: '',
    bank_name: '',
    bank_account: ''
  });

  useEffect(() => {
    fetchEmployee();
  }, [id]);

  const fetchEmployee = async () => {
    try {
      const response = await axios.get(`${API}/employees/${id}`);
      const employee = response.data;
      setFormData({
        user_id: employee.user_id || '',
        full_name: employee.full_name || '',
        email: employee.email || '',
        phone: employee.phone || '',
        department: employee.department || '',
        position: employee.position || '',
        join_date: employee.join_date || new Date().toISOString().split('T')[0],
        employment_type: employee.employment_type || 'full_time',
        salary_basic: employee.salary_basic || 0,
        salary_allowances: employee.salary_allowances || 0,
        ic_number: employee.ic_number || '',
        nationality: employee.nationality || 'malaysian',
        marital_status: employee.marital_status || 'single',
        spouse_working: employee.spouse_working || false,
        number_of_children: employee.number_of_children || 0,
        disabled_self: employee.disabled_self || false,
        disabled_spouse: employee.disabled_spouse || false,
        disabled_children: employee.disabled_children || 0,
        epf_number: employee.epf_number || '',
        socso_number: employee.socso_number || '',
        bank_name: employee.bank_name || '',
        bank_account: employee.bank_account || ''
      });
    } catch (error) {
      toast.error('Failed to fetch employee details');
      navigate('/suite/hr/employees');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...formData,
        salary_basic: parseFloat(formData.salary_basic) || 0,
        salary_allowances: parseFloat(formData.salary_allowances) || 0,
      };
      
      await axios.put(`${API}/employees/${id}`, payload);
      toast.success('Employee updated successfully!');
      navigate('/suite/hr/employees');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update employee');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900 dark:border-slate-100"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Button
            variant="outline"
            onClick={() => navigate('/suite/hr/employees')}
            className="flex items-center space-x-2"
          >
            <ArrowLeft className="h-4 w-4" />
            <span>Back to Employees</span>
          </Button>
          <div>
            <h2 className="text-2xl font-bold text-slate-900 dark:text-slate-100">Edit Employee</h2>
            <p className="text-slate-600 dark:text-slate-400 mt-1">{formData.full_name}</p>
          </div>
        </div>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="bg-white dark:bg-slate-800 rounded-xl border border-slate-100 dark:border-slate-700 p-6 shadow-sm">
        <div className="space-y-6">
          {/* Personal Information */}
          <div>
            <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-4">Personal Information</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Full Name *</Label>
                <Input
                  required
                  value={formData.full_name}
                  onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                  placeholder="John Doe"
                  className="dark:bg-slate-900 dark:border-slate-600"
                />
              </div>
              <div>
                <Label>Email *</Label>
                <Input
                  required
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="john@company.com"
                  className="dark:bg-slate-900 dark:border-slate-600"
                />
              </div>
              <div>
                <Label>Phone</Label>
                <Input
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="+60123456789"
                  className="dark:bg-slate-900 dark:border-slate-600"
                />
              </div>
              <div>
                <Label>IC Number</Label>
                <Input
                  value={formData.ic_number}
                  onChange={(e) => setFormData({ ...formData, ic_number: e.target.value })}
                  placeholder="990101-01-1234"
                  className="dark:bg-slate-900 dark:border-slate-600"
                />
              </div>
              <div>
                <Label>Nationality</Label>
                <select
                  value={formData.nationality}
                  onChange={(e) => setFormData({ ...formData, nationality: e.target.value })}
                  className="w-full h-10 rounded-md border border-slate-300 dark:border-slate-600 px-3 bg-white dark:bg-slate-900 dark:text-slate-100"
                >
                  <option value="malaysian">Malaysian</option>
                  <option value="non_malaysian_permanent">Non-Malaysian (Permanent Resident)</option>
                  <option value="non_malaysian_temporary">Non-Malaysian (Temporary)</option>
                </select>
              </div>
            </div>
          </div>

          {/* Employment Information */}
          <div className="border-t border-slate-200 dark:border-slate-700 pt-6">
            <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-4">Employment Information</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Department</Label>
                <Input
                  value={formData.department}
                  onChange={(e) => setFormData({ ...formData, department: e.target.value })}
                  placeholder="Engineering"
                  className="dark:bg-slate-900 dark:border-slate-600"
                />
              </div>
              <div>
                <Label>Position</Label>
                <Input
                  value={formData.position}
                  onChange={(e) => setFormData({ ...formData, position: e.target.value })}
                  placeholder="Senior Developer"
                  className="dark:bg-slate-900 dark:border-slate-600"
                />
              </div>
              <div>
                <Label>Join Date *</Label>
                <Input
                  required
                  type="date"
                  value={formData.join_date}
                  onChange={(e) => setFormData({ ...formData, join_date: e.target.value })}
                  className="dark:bg-slate-900 dark:border-slate-600"
                />
              </div>
              <div>
                <Label>Employment Type</Label>
                <select
                  value={formData.employment_type}
                  onChange={(e) => setFormData({ ...formData, employment_type: e.target.value })}
                  className="w-full h-10 rounded-md border border-slate-300 dark:border-slate-600 px-3 bg-white dark:bg-slate-900 dark:text-slate-100"
                >
                  <option value="full_time">Full Time</option>
                  <option value="part_time">Part Time</option>
                  <option value="contract">Contract</option>
                </select>
              </div>
            </div>
          </div>

          {/* Salary Information */}
          <div className="border-t border-slate-200 dark:border-slate-700 pt-6">
            <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-4">Salary Information</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Basic Salary (RM) *</Label>
                <Input
                  required
                  type="number"
                  step="0.01"
                  value={formData.salary_basic}
                  onChange={(e) => setFormData({ ...formData, salary_basic: e.target.value })}
                  placeholder="5000.00"
                  className="dark:bg-slate-900 dark:border-slate-600"
                />
              </div>
              <div>
                <Label>Allowances (RM)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.salary_allowances}
                  onChange={(e) => setFormData({ ...formData, salary_allowances: e.target.value })}
                  placeholder="500.00"
                  className="dark:bg-slate-900 dark:border-slate-600"
                />
              </div>
            </div>
          </div>

          {/* Tax Relief Information */}
          <div className="border-t border-slate-200 dark:border-slate-700 pt-6">
            <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-4">Tax Relief Information</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Marital Status</Label>
                <select
                  value={formData.marital_status}
                  onChange={(e) => setFormData({ ...formData, marital_status: e.target.value })}
                  className="w-full h-10 rounded-md border border-slate-300 dark:border-slate-600 px-3 bg-white dark:bg-slate-900 dark:text-slate-100"
                >
                  <option value="single">Single</option>
                  <option value="married">Married</option>
                </select>
              </div>
              {formData.marital_status === 'married' && (
                <div className="flex items-center space-x-2 pt-6">
                  <input
                    type="checkbox"
                    id="spouse_working"
                    checked={formData.spouse_working}
                    onChange={(e) => setFormData({ ...formData, spouse_working: e.target.checked })}
                    className="h-4 w-4 rounded border-slate-300 dark:border-slate-600"
                  />
                  <Label htmlFor="spouse_working" className="mb-0">Spouse Working</Label>
                </div>
              )}
              <div>
                <Label>Number of Children</Label>
                <Input
                  type="number"
                  min="0"
                  value={formData.number_of_children}
                  onChange={(e) => setFormData({ ...formData, number_of_children: parseInt(e.target.value) || 0 })}
                  className="dark:bg-slate-900 dark:border-slate-600"
                />
              </div>
              <div>
                <Label>Disabled Children</Label>
                <Input
                  type="number"
                  min="0"
                  value={formData.disabled_children}
                  onChange={(e) => setFormData({ ...formData, disabled_children: parseInt(e.target.value) || 0 })}
                  className="dark:bg-slate-900 dark:border-slate-600"
                />
              </div>
              <div className="flex items-center space-x-2 pt-6">
                <input
                  type="checkbox"
                  id="disabled_self"
                  checked={formData.disabled_self}
                  onChange={(e) => setFormData({ ...formData, disabled_self: e.target.checked })}
                  className="h-4 w-4 rounded border-slate-300 dark:border-slate-600"
                />
                <Label htmlFor="disabled_self" className="mb-0">Employee is Disabled</Label>
              </div>
              {formData.marital_status === 'married' && (
                <div className="flex items-center space-x-2 pt-6">
                  <input
                    type="checkbox"
                    id="disabled_spouse"
                    checked={formData.disabled_spouse}
                    onChange={(e) => setFormData({ ...formData, disabled_spouse: e.target.checked })}
                    className="h-4 w-4 rounded border-slate-300 dark:border-slate-600"
                  />
                  <Label htmlFor="disabled_spouse" className="mb-0">Spouse is Disabled</Label>
                </div>
              )}
            </div>
          </div>

          {/* Statutory Information */}
          <div className="border-t border-slate-200 dark:border-slate-700 pt-6">
            <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-4">Statutory Numbers</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>EPF Number</Label>
                <Input
                  value={formData.epf_number}
                  onChange={(e) => setFormData({ ...formData, epf_number: e.target.value })}
                  placeholder="EPF123456"
                  className="dark:bg-slate-900 dark:border-slate-600"
                />
              </div>
              <div>
                <Label>SOCSO Number</Label>
                <Input
                  value={formData.socso_number}
                  onChange={(e) => setFormData({ ...formData, socso_number: e.target.value })}
                  placeholder="SOCSO123456"
                  className="dark:bg-slate-900 dark:border-slate-600"
                />
              </div>
              <div>
                <Label>Bank Name</Label>
                <Input
                  value={formData.bank_name}
                  onChange={(e) => setFormData({ ...formData, bank_name: e.target.value })}
                  placeholder="Maybank"
                  className="dark:bg-slate-900 dark:border-slate-600"
                />
              </div>
              <div>
                <Label>Bank Account</Label>
                <Input
                  value={formData.bank_account}
                  onChange={(e) => setFormData({ ...formData, bank_account: e.target.value })}
                  placeholder="123456789012"
                  className="dark:bg-slate-900 dark:border-slate-600"
                />
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="border-t border-slate-200 dark:border-slate-700 pt-6 flex justify-end space-x-3">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate('/suite/hr/employees')}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              className="bg-slate-900 hover:bg-slate-800 dark:bg-slate-700 dark:hover:bg-slate-600"
              disabled={saving}
            >
              <Save className="h-4 w-4 mr-2" />
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
}
